import useCancelReturnRequest from "@babackapp/private-api-client/client/use-cancel-return-request";
import useCancellationRefundPreview from "@babackapp/private-api-client/client/use-cancellation-refund-preview";
import useTranslation from "@babackapp/translations/use-translation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@babackapp/ui/components/alert-dialog";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useCurrentReturnRequest } from "../../../utils/current-return-request-context";

interface CancelReturnAlertDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export default function CancelReturnAlertDialog({
  isOpen,
  onOpenChange,
}: CancelReturnAlertDialogProps) {
  const { data: shop } = useCurrentShopSuspense();
  const returnRequest = useCurrentReturnRequest();
  const t = useTranslation();
  const { mutate } = useCancelReturnRequest();

  // Computed by the server with the cancellation's own rules: the refund
  // depends on Shopify (exchange orders) and Stripe (what is left).
  const {
    data: preview,
    isFetching,
    isError,
  } = useCancellationRefundPreview({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
    enabled: isOpen,
  });
  const amount =
    preview?.amount != null && preview.currency
      ? new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: preview.currency,
        }).format(preview.amount)
      : "";
  // isFetching, not isLoading: on reopen the last answer must not be shown
  // as current while it is read again.
  const refundNotice = isFetching
    ? t("returnDetail.cancelRefund.loading")
    : preview?.status === "refund"
      ? t("returnDetail.cancelRefund.refund", { amount })
      : preview?.status === "kept"
        ? t("returnDetail.cancelRefund.kept")
        : preview?.status === "by-hand"
          ? t("returnDetail.cancelRefund.byHand", { amount })
          : isError || preview?.status === "unknown"
            ? t("returnDetail.cancelRefund.unknown")
            : null;

  const handleCancelReturn = () => {
    mutate({
      returnRequestId: returnRequest.id,
      shopId: shop.id,
    });
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("returnDetail.cancelReturnRequestTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("returnDetail.cancelReturnRequest")}
            {refundNotice && (
              <>
                <br />
                <br />
                <strong>{refundNotice}</strong>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("misc.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={handleCancelReturn} disabled={isFetching}>
            {t("misc.continue")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
